<template>
  <div v-if="internalAnalytic">
    <slot></slot>
    <div v-if="admin" class="form-section">
      <fieldset class="fieldset">
        <legend>Analytic Status</legend>
        <b-switch v-model="internalAnalytic.released">
          Released
        </b-switch>
      </fieldset>
    </div>

    <div class="form-section">
      <fieldset class="fieldset">
        <legend>Analytic Description</legend>

        <div class="field">
          <div class="field-body">
            <form-field icon="name" title="Analytic Name" classes="analytic-name is-narrow">
              <input v-model="internalAnalytic.name"
                class="input"
                type="text"
                placeholder="Name">
            </form-field>
            <form-field icon="level" title="Level" classes="analytic-level is-narrow">
              <input v-model="internalAnalytic.level"
                class="input"
                type="number">
            </form-field>
          </div>
        </div>

        <form-field icon="category" title="Categories">
          <b-taginput
            v-model="internalAnalytic.categories"
            :data="categoriesOptions"
            autocomplete
            :allow-new="true"
            icon-pack="fas"
            icon="chevron-right"
            placeholder="Add a Category"
            @typing="getFilteredTags"
            :open-on-focus="true"
            class="analytic-tag-selection">
          </b-taginput>
        </form-field>

        <form-field icon="description" title="Description">
          <textarea
            v-model="internalAnalytic.description"
            class="textarea"
            type="text"
            placeholder="Description"></textarea>
        </form-field>
      </fieldset>
    </div>

    <div class="form-section">
      <fieldset class="fieldset">
        <legend>Analytic Filter</legend>
        <form-field icon="sensor" title="Sensor Type" classes="analytic-input-type is-narrow">
          <multiselect
            v-model="internalAnalytic.inputType"
            :options="inputTypeOptions"
            :searchable="false"
            :close-on-select="true"
            :show-labels="false"
            :allow-empty="false"
            placeholder="Pick a Event Type">
          </multiselect>
        </form-field>

        <div v-if="qbEnabled" class="tabs">
          <ul>
            <li :class="{'is-active': qbView }">
              <a @click="qbView = !qbView">
                <span class="icon">
                  <svgicon
                    name="querybuilder"
                    width="24"
                    height="24">
                  </svgicon>
                </span>
                <span>Filter Query Builder View</span>
              </a>
            </li>
            <li :class="{'is-active': !qbView }">
              <a @click="qbView = !qbView">
                <span class="icon">
                  <svgicon
                    name="textview"
                    width="24"
                    height="24"></svgicon>
                </span>
                <span>Filter Text View</span>
              </a>
            </li>
          </ul>
        </div>
        <query-builder
          v-if="qbEnabled && qbView && eventDef"
          :def="eventDef"
          :filter.sync="qbFilter">
        </query-builder>
        <div v-else class="field">
          <textarea
            v-model="textFilter"
            class="textarea"
            type="text"
            placeholder="Description"></textarea>
        </div>
      </fieldset>
    </div>

    <div class="form-section">
      <fieldset class="fieldset">
        <legend>Analytic Actions</legend>
        <actions-selection
          :actions.sync="internalAnalytic.actions">
        </actions-selection>
        <form-field icon="tag" title="Tags">
          <b-taginput
            v-model="internalAnalytic.tags"
            :data="tagOptions"
            autocomplete
            :allow-new="true"
            icon-pack="fas"
            icon="chevron-right"
            placeholder="Add a Tag"
            @typing="getFilteredTags"
            :open-on-focus="true">
          </b-taginput>
        </form-field>
      </fieldset>
    </div>

    <div class="form-section">
      <fieldset class="fieldset">
        <legend>Analytic Context Items</legend>
        <div class="field">
          <a v-on:click="addContextItem(null)" class="button add">
            <i class="fas fa-plus"></i>
            Add Context Item
          </a>
          <div
            v-for="(item,index) in internalAnalytic.context"
            :key="item.key"
            class="context-items">
            <analytic-context-item
              v-if="eventDef"
              :contextItem.sync="item"
              :eventDef="eventDef"
              :index="index"
              :deleteMethod="removeContextItem">
              <button @click="removeContextItem(index)" class="delete"></button>
            </analytic-context-item>
          </div>
        </div>
      </fieldset>
    </div>

    <div class="form-section">
      <fieldset class="fieldset">
        <legend>Snapshot Files</legend>
        <a v-on:click="addSnapshotFile()" class="button add snapshot-button">
          <i class="fas fa-plus"></i>
          Add Snapshot File
        </a>
        <div
        v-for="file in internalAnalytic.snapshotFiles"
        :key="file.key"
        class="field is-horizontal snapshot-field">
        <a
          v-on:click="removeSnapshotFile(file.key)"
          class="button button-inverse full-height">
          <i class="fas fa-times"></i>
        </a>
        <div class="field-body">
          <div class="field">
            <div class="control">
              <input v-model="file.value"
                class="input"
                type="text"
                placeholder="FilePath">
            </div>
          </div>
        </div>
      </div>
      </fieldset>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import analyticMixin from '@/mixins/analytic';
import AnalyticContextItem from '@/components/analytics/forms/AnalyticContextItem.vue';
import Multiselect from 'vue-multiselect';
import QueryBuilder from '@/components/analytics/editor/QueryBuilder.vue';
import FormField from '@/components/forms/FormField.vue';
import ActionsSelection from '@/components/analytics/forms/ActionsSelection.vue';

export default {
  name: 'AnalyticForm',
  mixins: [analyticMixin],
  components: {
    Multiselect,
    AnalyticContextItem,
    QueryBuilder,
    FormField,
    ActionsSelection,
  },
  props: {
    analytic: {
      required: true,
    },
    admin: {
      type: Boolean,
      default: false,
    },
  },
  created() {
    this.internalAnalytic = this.generateInternalAnalytic(this.analytic, this.admin);
    this.setEventDef();
    if (this.internalAnalytic.filter === '') {
      this.qbEnabled = true;
    } else {
      this.textFilter = this.internalAnalytic.filter;
    }
  },
  async mounted() {
    const categories = await this.$store.dispatch('primary/listAnalyticsCategories');
    if (categories) {
      this.categoriesOptions = _.map(categories, cat => cat.value);
    }
    const tags = await this.$store.dispatch('primary/listAnalyticsTags');
    debugger;
    if (tags) {
      this.tagOptions = _.map(tags, tag => tag.value);
    }
  },
  computed: {
    filterValue() {
      if (this.qbEnabled && this.qbView) {
        return this.qbFilter;
      }
      return this.textFilter;
    },
    syncedAnalytic() {
      if (this.internalAnalytic) {
        const analytic = {
          uuid: this.internalAnalytic.uuid,
          actions: this.internalAnalytic.actions,
          categories: this.internalAnalytic.categories,
          context: _.map(this.internalAnalytic.context, c => _.omit(c, ['key'])),
          tags: this.internalAnalytic.tags,
          description: this.internalAnalytic.description,
          filter: this.internalAnalytic.filter,
          inputType: this.internalAnalytic.inputType,
          level: this.internalAnalytic.level,
          name: this.internalAnalytic.name,
          snapshotFiles: _.map(this.internalAnalytic.snapshotFiles, f => f.value),
        };
        if (this.admin) {
          analytic.released = this.internalAnalytic.released;
        }
        return analytic;
      }
      return null;
    },
  },
  methods: {
    getFilteredTags(data) {
      console.log(data);
    },
    test(event, index) {
      this.context[index] = event;
    },
    addSnapshotFile() {
      this.internalAnalytic.snapshotFiles.push({
        key: this.uuid(),
        value: '',
      });
    },
    removeSnapshotFile(key) {
      const index = _.findIndex(this.internalAnalytic.snapshotFiles, { key });
      this.internalAnalytic.snapshotFiles.splice(index, 1);
    },
    removeContextItem(index) {
      this.internalAnalytic.context.splice(index, 1);
    },
    addContextItem(value) {
      const item = {
        key: this.uuid(),
        name: '',
        type: 'String',
        exprs: [''],
      };

      if (value) {
        item.type = value.type;
        item.name = value.name;
        item.exprs = value.exprs;
      }

      this.internalAnalytic.context.push(item);
    },
    addTag(tag) {
      this.internalAnalytic.tags.push(tag);
    },
    removeTag(tag) {
      this.internalAnalytic.tags = this.internalAnalytic.tags.filter(t => t !== tag);
    },
    addCategory(category) {
      this.internalAnalytic.categories.push(category);
    },
    removeCategory(category) {
      this.internalAnalytic.categories = this.internalAnalytic.categories
        .filter(c => c !== category);
    },
    addAction(action) {
      this.internalAnalytics.actions.push(action);
    },
    removeAction(action) {
      this.internalAnalytic.actions = this.internalAnalytic.actions.filter(a => a !== action);
    },
    setEventDef() {
      if (_.has(this.$typeDefs.events, this.internalAnalytic.inputType)) {
        this.eventDef = this.$typeDefs.events[this.internalAnalytic.inputType];
        this.$emit('update:analyticEventDef', this.eventDef);
      }
    },
  },
  watch: {
    // eslint-disable-next-line
    'internalAnalytic.inputType': function () {
      this.setEventDef();
    },
    syncedAnalytic: {
      deep: true,
      handler() {
        this.$emit('update:analytic', this.syncedAnalytic);
      },
    },
    filterValue() {
      this.internalAnalytic.filter = this.filterValue;
    },
  },
  data() {
    return {
      internalAnalytic: null,
      qbEnabled: false,
      cardHeader: false,
      textFilter: '',
      qbFilter: '',
      qbView: true,
      eventDef: null,
      inputTypeOptions: _.map(this.$typeDefs.events, 'type'),
      actionOptions: [
        'Alert',
        'RemoteLog',
        'LocalLog',
        'Cache',
      ],
      tagOptions: [],
      categoriesOptions: [],
    };
  },
};
</script>

<style lang="scss" scoped>
.analytic-name {
  width: 300px;
}

.snapshot-field {
  margin-top: 1rem;
}

button.delete {
  position: absolute;
  right: 0.5rem;
  top: 0.5rem;
}

.tabs {
  a {
    color: lightslategray;
  }

  a:hover {
    color: whitesmoke;
    border-bottom: 0px;
  }
}
</style>
